/**
 * Availability derivation — pure functions behind the per-unit
 * availability timelines (AvailabilityRibbon / AvailabilityCard).
 *
 * The EMS edge uplinks telemetry on a steady cadence, so "was this unit
 * reporting?" is derivable from the SPACING of its history samples — no
 * dedicated heartbeat series needed. This module turns a sampled series
 * + an expected cadence into online/gap segments and an uptime %.
 *
 * RULES (the contract for every consumer):
 *
 *  1. Interior gap — a gap opens between two consecutive samples when
 *     their spacing exceeds 3 × `expectedCadenceMs` (GAP_FACTOR). Data
 *     existed on BOTH sides, so we demand a real hole (not one missed
 *     uplink) before calling it an outage. The gap is drawn from one
 *     cadence step after the last good sample (the first MISSED expected
 *     sample) to the next real sample.
 *
 *  2. Edge gaps — when data starts late / ends early relative to the
 *     requested window, the leading/trailing stretch counts as a gap.
 *     Edges use a TIGHTER threshold (1.5 × cadence, floored at 5 min):
 *     the absence is open-ended there — the unit may still be down — so
 *     we flag earlier than the interior rule. The 5-min absolute floor
 *     keeps live now-anchored windows from flagging a trailing gap
 *     between two routine uplinks at fine cadences.
 *
 *  3. Jitter merge — online islands shorter than 60 s between two gaps
 *     (a lone straggler sample inside an outage) are merged into one
 *     gap so the ribbon reads as one incident, not two.
 *
 *  4. uptimePct = (window − totalGapMs) / window × 100, clamped to
 *     [0, 100]. A window with no samples at all is one full-width gap
 *     (uptime 0) — honest for an availability view.
 *
 * Samples with `value: null` (or non-finite values) carry no data and
 * are treated as absent. Samples outside [start, end] are ignored.
 * Pure + deterministic: same inputs → same segments.
 */

/** Interior gap threshold, in expected-cadence multiples. */
export const GAP_FACTOR = 3;
/** Leading/trailing gap threshold, in expected-cadence multiples. */
export const EDGE_FACTOR = 1.5;
/** Absolute floor for the edge threshold (ms) — anti-flicker on live windows. */
export const EDGE_MIN_MS = 5 * 60_000;
/** Online islands shorter than this between two gaps merge into one gap. */
export const JITTER_MERGE_MS = 60_000;

export type AvailabilityState = "online" | "gap";

export interface AvailabilitySegment {
  start: number;
  end: number;
  state: AvailabilityState;
}

export interface AvailabilityResult {
  /** Contiguous partition of [start, end] into online/gap stretches. */
  segments: AvailabilitySegment[];
  /** 0–100. */
  uptimePct: number;
  /** Sum of all gap-segment durations (ms). */
  totalGapMs: number;
}

export function deriveAvailability(
  samples: Array<{ ts: number; value: number | null }>,
  opts: { start: number; end: number; expectedCadenceMs: number },
): AvailabilityResult {
  const { start, end, expectedCadenceMs: cad } = opts;
  const span = end - start;
  if (!(span > 0) || !(cad > 0)) {
    return { segments: [], uptimePct: 100, totalGapMs: 0 };
  }

  // Valid sample timestamps, in-window, ascending. Defensive sort — the
  // adapters return ascending series, but pure functions don't assume.
  const ts = samples
    .filter((s) => s.value !== null && Number.isFinite(s.value) && s.ts >= start && s.ts <= end)
    .map((s) => s.ts)
    .sort((a, b) => a - b);

  const interiorThreshold = GAP_FACTOR * cad;
  const edgeThreshold = Math.max(EDGE_FACTOR * cad, EDGE_MIN_MS);

  // ---- collect raw gaps (rule 1 + 2) --------------------------------
  const gaps: Array<{ start: number; end: number }> = [];
  if (ts.length === 0) {
    gaps.push({ start, end });
  } else {
    if (ts[0] - start > edgeThreshold) {
      gaps.push({ start, end: ts[0] });
    }
    for (let i = 1; i < ts.length; i++) {
      if (ts[i] - ts[i - 1] > interiorThreshold) {
        gaps.push({ start: Math.min(ts[i - 1] + cad, ts[i]), end: ts[i] });
      }
    }
    const last = ts[ts.length - 1];
    if (end - last > edgeThreshold) {
      gaps.push({ start: Math.min(last + cad, end), end });
    }
  }

  // ---- jitter merge (rule 3) ----------------------------------------
  const merged: Array<{ start: number; end: number }> = [];
  for (const g of gaps) {
    const prev = merged[merged.length - 1];
    if (prev && g.start - prev.end < JITTER_MERGE_MS) {
      prev.end = Math.max(prev.end, g.end);
    } else {
      merged.push({ ...g });
    }
  }

  // ---- partition the window into segments ---------------------------
  const segments: AvailabilitySegment[] = [];
  let cursor = start;
  let totalGapMs = 0;
  for (const g of merged) {
    if (g.start > cursor) segments.push({ start: cursor, end: g.start, state: "online" });
    segments.push({ start: g.start, end: g.end, state: "gap" });
    totalGapMs += g.end - g.start;
    cursor = g.end;
  }
  if (cursor < end) segments.push({ start: cursor, end, state: "online" });

  const uptimePct = Math.min(100, Math.max(0, ((span - totalGapMs) / span) * 100));
  return { segments, uptimePct, totalGapMs };
}

// ---------------------------------------------------------------------------
// Window → fetch plan
// ---------------------------------------------------------------------------

/** Granularities the availability fetch uses (subset of HistoricalQuery's). */
export type AvailabilityGran = "raw" | "15m" | "h";

export interface AvailabilityFetchPlan {
  /** history() granularity for this window span. */
  gran: AvailabilityGran;
  /**
   * Expected sample cadence at that granularity (ms) — feeds
   * deriveAvailability. For bucketed grans this is the bucket step; for
   * raw it is the edge's documented ~2-min uplink cadence.
   */
  expectedCadenceMs: number;
  /**
   * Snap quantum for the query start (ms). Aligning the window start to
   * the bucket grid keeps the fetched sample grid stable across renders
   * (stable useQuery keys) and keeps bucket boundaries deterministic, so
   * a sub-hour outage always swallows whole buckets instead of
   * phase-luck partial ones.
   */
  snapMs: number;
}

const MIN = 60_000;
const HOUR = 3_600_000;

/**
 * Pick fetch granularity by window span: raw ≤ 6 h, 15 m ≤ 48 h, hourly
 * beyond. Gap detectability scales with the cadence on purpose — at a
 * 7-day zoom an outage shorter than ~3 h is sub-resolution, exactly like
 * any other chart at that granularity.
 */
export function availabilityFetchPlan(spanMs: number): AvailabilityFetchPlan {
  if (spanMs <= 6 * HOUR) {
    // "raw" = every ~2-min edge uplink (see lib/timeRange GRAN_LABEL).
    return { gran: "raw", expectedCadenceMs: 2 * MIN, snapMs: MIN };
  }
  if (spanMs <= 48 * HOUR) {
    return { gran: "15m", expectedCadenceMs: 15 * MIN, snapMs: 15 * MIN };
  }
  return { gran: "h", expectedCadenceMs: HOUR, snapMs: HOUR };
}
